import { capitalize } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";

export interface PublicMenuItem {
  id: string;
  name: string;
  description: string;
  price: string;
}

export interface PublicMenuSection {
  section: string;
  items: PublicMenuItem[];
}

// Raw shapes as returned by the public menu endpoint (MenuListResponse).
interface MenuItemDto {
  id: string;
  name: string;
  description: string;
  price: string;
  featured: boolean;
  priority: number;
}

interface MenuSectionDto {
  section: string;
  active: boolean;
  menuItems: MenuItemDto[];
}

const mapSections = (sections: MenuSectionDto[]): PublicMenuSection[] =>
  sections
    .filter((s) => s.active)
    .map((s) => ({
      section: s.section,
      items: [...s.menuItems]
        .sort((a, b) => a.priority - b.priority)
        .map((i) => ({
          id: i.id,
          name: i.name,
          description: i.description,
          price: i.price,
        })),
    }));

// Slug-addressed like useGetPublicRestaurant: /restaurants/public/{city}/{name}
// with /menu appended. Runs in parallel with the restaurant-info query — it does
// not depend on a resolved restaurant id.
export const useGetPublicRestaurantMenu = (name: string, city: string) =>
  useQuery({
    queryKey: ["public-restaurant-menu", name, city],
    queryFn: async (): Promise<PublicMenuSection[]> => {
      const res = await fetch(
        `${API_URL}/restaurants/public/${capitalize(city)}/${name}/menu`,
      );
      if (res.status === 404) throw new Error("NOT_FOUND");
      const data = await res.json();
      if (data.success)
        return mapSections((data.menuSections ?? []) as MenuSectionDto[]);
      throw new Error("Failed to fetch menu");
    },
    enabled: !!name && !!city,
  });
